import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import NavigationBar from "./app/main/components/navigation/navigationBar";
import Login from "./app/main/pages/authentication/login";
import Signup from "./app/main/pages/authentication/signup";
import Recover from "./app/main/pages/authentication/recover";
import Reset from "./app/main/pages/authentication/reset";
import Submit from "./app/main/pages/authentication/submit";
import PrivateRoute from "./privateroutes/PrivateRoute";
import CallScreen from "./app/main/pages/chatCall/CallScreen";
// import VideoCallScreen from "./app/main/pages/chatCall/VideoCallScreen";
import Emails from "./app/main/pages/emailModule";
import NewEmail from "./app/main/pages/emailModule/emailWindow/newEmail/NewEmail";
import { Add } from "./app/main/pages/emailModule/emailWindow/emailRules/AddRules/Add";
import { Manage } from "./app/main/pages/emailModule/emailWindow/emailRules/ManageRules/Manage";
import EmailWindow from "./app/main/pages/emailModule/emailWindow/emailWindow";
import NewFolders from "./app/main/pages/emailModule/AllFolders/NewFolders";
import { getFolders } from "./app/services/api/apiManager";
import { useDispatch, useSelector } from "react-redux";
import CallNotification from "./app/main/pages/chatCall/CallNotification";
import GroupCallNotification from "./app/main/pages/chatCall/GroupCallNotification";
import { Outlet } from "react-router-dom";

import {
  setClearIncomingCall,
  setIsSendingRoomData,
} from "./app/services/store/slice/CallSlice";
import { useMediaQuery, useTheme } from "@mui/material";
import Reply from "./app/main/pages/emailModule/emailWindow/Reply";
// import SentMails from "./app/main/pages/emailModule/emailWindow/sentMails/SentMails";
// import DraftMails from "./app/main/pages/emailModule/emailWindow/draftMails/DraftMails";
// import TrashMails from "./app/main/pages/emailModule/emailWindow/trash/TrashMails";
// import FavouriteMails from "./app/main/pages/emailModule/emailWindow/favouriteMails/FavouriteMails";
import LoadingSpinner from "./app/main/pages/chats/chatWindow/loadingSpinner/LoadingSpinner";
import { Contacts } from "./app/main/pages/contacts/Contacts";
import DraftMessages from "./app/main/pages/chats/chatWindow/DraftMessages";
import { useTwilio } from "./app/services/context/TwilioContext";
// import Ticket from "./app/main/pages/Ticket";
// import TicketDetail from "./app/main/components/Tickets/ViewTicket";
// Lazy loaded components
const Chats = lazy(() => import("./app/main/pages/chats/Chats"));
const Users = lazy(() => import("./app/main/pages/users/Users"));
const AdminPanel = lazy(() => import("./app/main/pages/adminPanel/AdminPanel"));
const Meetings = lazy(() => import("./app/main/pages/meetings/index"));
const UserInfo = lazy(() => import("./app/main/pages/userInfo/UserInfo"));
const Task = lazy(
  () => import("./app/main/pages/tasks/TaskDashboard/TaskDashboard"),
);
const Inbox = lazy(
  () => import("./app/main/pages/emailModule/emailWindow/inbox/Inbox"),
);
const Draft = lazy(
  () =>
    import("./app/main/pages/emailModule/emailWindow/draftMails/DraftMails"),
);
const Favourite = lazy(
  () =>
    import("./app/main/pages/emailModule/emailWindow/favouriteMails/FavouriteMails"),
);
const Sent = lazy(
  () => import("./app/main/pages/emailModule/emailWindow/sentMails/SentMails"),
);
const Trash = lazy(
  () => import("./app/main/pages/emailModule/emailWindow/trash/TrashMails"),
);

function AppRoutes() {
  const {
    callAccepted,
    incomingCall,
    setIncomingCall,
    setCallStatus,
    setCallEnded,
  } = useTwilio();
  const { isSendingRoomData } = useSelector((state) => state.call);
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const userRole = JSON.parse(localStorage.getItem("user_roles"));
  const renderFolder = useSelector((state) => state?.chat?.renderFolder);
  const [folderData, setFolderData] = useState([]);
  const [render, setRender] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // region getFolders
  const getFolder = async (id) => {
    try {
      const response = await getFolders(id);
      if (response?.status === 200) {
        setFolderData(response?.data?.result);
        // dispatch(setRenderAfterDeleteFolder(response?.data?.result));
      }
    } catch (error) {
      console.warn(error);
    }
  };
  useEffect(() => {
    if (userId) {
      getFolder(userId);
    }
  }, [render]);
  //
  useEffect(() => {
    setRender(renderFolder);
  }, [renderFolder]);

  const handleCancel = () => {
    if (!callAccepted) {
      setCallStatus("Call canceled");
      setIncomingCall(null);
      dispatch(setClearIncomingCall(true));
      setCallEnded(true);
    }
  };
  useEffect(() => {
    // Only Twilio Call objects expose .on/.off. WebRTC in-app incoming calls set
    // incomingCall to a plain object ({ webrtc: true, ... }); their cancel/end is
    // handled via the webRtcCallService subscription in TwilioContext instead.
    if (incomingCall && typeof incomingCall.on === "function") {
      incomingCall.on("cancel", handleCancel);
    }
    return () => {
      if (incomingCall && typeof incomingCall.off === "function") {
        incomingCall.off("cancel", handleCancel);
      }
    };
  }, [callAccepted, incomingCall]);

  // Group Call TimeOut Rendering Call Logs

  useEffect(() => {
    if (isSendingRoomData?.End === false) {
      setCallEnded(true);
      dispatch(setIsSendingRoomData(null));
    }
  }, [isSendingRoomData]);

  return (
    <>
      {/* Show call notification if there's an incoming call */}
      {incomingCall && <PrivateRoute element={<CallNotification />} />}
      {isSendingRoomData?.End === true &&
        isSendingRoomData?.ToUserIds?.length > 0 && (
          <PrivateRoute element={<GroupCallNotification />} />
        )}
      <DraftMessages />
      <Routes>
        {/* Authentication Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/recover" element={<Recover />} />
        <Route path="/reset" element={<Reset />} />
        <Route path="/submit" element={<Submit />} />
        <Route
          path="/api/RCCall/oauth/callback"
          element={<Navigate to="/chats" replace />}
        />

        {/* Protected Routes */}
        <Route
          element={isMobile ? <Outlet /> : <NavigationBar />} // Ensure Outlet is rendered for mobile
        >
          {/* Common Protected Routes */}
          <Route path="/emails" element={<PrivateRoute element={<Emails />} />}>
            <Route path="inbox" element={<Inbox />} />
            <Route path="newEmail" element={<NewEmail />} />
            <Route path="sent" element={<Sent />} />
            <Route path="draft" element={<Draft />} />
            <Route path="favourite" element={<Favourite />} />
            <Route path="trash" element={<Trash />} />
            <Route path="add" element={<Add />} />
            <Route path="manage" element={<Manage />} />
            <Route path="window" element={<EmailWindow />} />
            {folderData?.map((folder) => (
              <Route
                key={folder.folderId}
                path={`folder/${folder.folderId}`}
                element={<NewFolders />}
              />
            ))}
          </Route>

          <Route
            path="/chats"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<Chats />} />
              </Suspense>
            }
          />
          <Route
            path="/tasks"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<Task />} />
              </Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<Users />} />
              </Suspense>
            }
          />
          <Route
            path="/meetings"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<CallScreen />} />
              </Suspense>
            }
          />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute
                  element={
                    userRole?.some(
                      (ur) =>
                        ur?.roleName?.toLowerCase() === "admin" ||
                        ur?.roleName?.toLowerCase() === "manager",
                    ) ? (
                      <AdminPanel />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
              </Suspense>
            }
          />
          {/* )} */}

          <Route
            path="/contacts"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<Contacts />} />
              </Suspense>
            }
          />
          {/* <Route
            path="/tickets"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<Ticket />} />
              </Suspense>
            }
          />

          <Route path="/tickets/ticket-detail/:id" element={<TicketDetail />} /> */}

          <Route
            path="/admin/info"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivateRoute element={<UserInfo />} />
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default AppRoutes;
